import React, { useState } from 'react';
import { SearchIcon, ShieldCheckIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Field, Input, Select } from '../ui/Field';
import { campusOptions } from '../../data/institutions';
import { naira } from '../../utils/format';
import type { PropertyKind, SearchIntent } from '../../types/housing';
const KINDS: PropertyKind[] = ['Hostel', 'Student residence', 'Apartment', 'Shared apartment', 'Self-contained', 'Co-living'];
const BUDGETS = [300000, 450000, 600000, 750000, 900000, 1200000];
interface SearchIntentPanelProps {
  initial: SearchIntent;
  onSearch: (intent: SearchIntent) => void;
}
export function SearchIntentPanel({
  initial,
  onSearch
}: SearchIntentPanelProps) {
  const [intent, setIntent] = useState<SearchIntent>(initial);
  function toggleKind(kind: PropertyKind) {
    setIntent(current => ({
      ...current,
      kinds: current.kinds.includes(kind) ? current.kinds.filter(item => item !== kind) : [...current.kinds, kind]
    }));
  }
  return <div className="mx-auto max-w-3xl" data-id="element-2104" data-mp="2kcs7uzhm1xef">
      <div className="text-center" data-id="element-2105" data-mp="2patpdfze1ons">
        <h1 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-2106" data-mp="2p9s3eggu3hra">
          Find somewhere to live
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-ink-muted" data-id="element-2107" data-mp="2bhm03uys5599">
          Tell us the campus you are studying at, what you can afford and when you need to move in.
          We will show verified and unverified listings side by side, clearly labelled.
        </p>
      </div>

      <form className="mt-8 rounded-card border border-line bg-surface p-5 shadow-panel sm:p-6" onSubmit={event => {
      event.preventDefault();
      onSearch(intent);
    }} data-id="element-2108" data-mp="26lwxtjvdla99">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2109" data-mp="1wo90qnaarqnx">
          <div className="lg:col-span-2" data-id="element-2110" data-mp="3mzd9yxhw3hak">
            <Field label="Campus" htmlFor="intent-campus" data-id="element-2111" data-mp="3r0gnvegdn395">
              <Select id="intent-campus" value={intent.campusId} onChange={event => setIntent(current => ({
              ...current,
              campusId: event.target.value
            }))} data-id="element-2112" data-mp="3qzx7i26dkmx8">
                {campusOptions.map(option => <option key={option.id} value={option.id} data-id="element-2113" data-mp="url3usdegv57">
                    {option.label}
                  </option>)}
              </Select>
            </Field>
          </div>
          <Field label="Budget per year" htmlFor="intent-budget" data-id="element-2114" data-mp="1mhpl4i5v5227">
            <Select id="intent-budget" value={String(intent.budgetMax)} onChange={event => setIntent(current => ({
            ...current,
            budgetMax: Number(event.target.value)
          }))} data-id="element-2115" data-mp="1ihynpvltznop">
              {BUDGETS.map(budget => <option key={budget} value={budget} data-id="element-2116" data-mp="267jh78fm23c8">
                  Up to {naira(budget)}
                </option>)}
            </Select>
          </Field>
          <Field label="Move-in date" htmlFor="intent-date" data-id="element-2117" data-mp="28r66q9ds9nji">
            <Input id="intent-date" type="date" value={intent.moveIn} onChange={event => setIntent(current => ({
            ...current,
            moveIn: event.target.value
          }))} data-id="element-2118" data-mp="28t0r9ml57dn2" />
          </Field>
        </div>

        <fieldset className="mt-5" data-id="element-2119" data-mp="2ms0fwu8nnsj8">
          <legend className="mb-2 text-[13px] font-medium text-ink" data-id="element-2120" data-mp="2rq1vv6asbbqh">
            Accommodation type{' '}
            <span className="font-normal text-ink-subtle" data-id="element-2121" data-mp="2wobszmg2kmeq">
              — leave blank to see everything
            </span>
          </legend>
          <div className="flex flex-wrap gap-2" data-id="element-2122" data-mp="31g3kq0ayoy6g">
            {KINDS.map(kind => {
            const active = intent.kinds.includes(kind);
            return <button key={kind} type="button" aria-pressed={active} onClick={() => toggleKind(kind)} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${active ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line-strong bg-surface text-ink-muted hover:border-ink-subtle hover:text-ink'}`} data-id="element-2123" data-mp="3ah1hpymzg5fj">
                  {kind}
                </button>;
          })}
          </div>
        </fieldset>

        <div className="mt-6 flex flex-col gap-3 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-2124" data-mp="2ivl5no2ia2h7">
          <p className="flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-2125" data-mp="2ssh6r8q2rmbq">
            <ShieldCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-700" data-id="element-2126" data-mp="2nt4kap348h9o" />
            Campuzon never asks you to pay a landlord through us. Rent collection is not enabled
            yet, and will be performed by a licensed payment institution when it is.
          </p>
          <Button type="submit" variant="primary" icon={<SearchIcon className="h-4 w-4" data-id="element-2128" data-mp="1kzzacgmzzv3m" />} data-id="element-2127" data-mp="3500s3wjwifw">
            Search housing
          </Button>
        </div>
      </form>
    </div>;
}